// Tiny DOM helpers shared by every screen.
import { SHELL_HOLES } from '../sim/shells.js';

export const h = (tag, attrs = {}, ...kids) => {
  const el = document.createElement(tag);
  for (const [k, v] of Object.entries(attrs)) {
    if (k === 'class') el.className = v;
    else if (k.startsWith('on')) el.addEventListener(k.slice(2), v);
    else if (v !== false && v != null) el.setAttribute(k, v);
  }
  for (const c of kids.flat()) if (c != null && c !== false) el.append(c.nodeType ? c : document.createTextNode(String(c)));
  return el;
};

// Moon drawn on a canvas from the phase index (0 new .. 4 full): pixels in the
// HD-2D look, a smooth paper disc in the 影絵 look.
export const moonIcon = (phase, size = 44) => {
  const smooth = document.body.classList.contains('look-kagee');
  const N = smooth ? 96 : 16;
  const cv = h('canvas', { width: N, height: N, class: 'moon-icon', style: `width:${size}px;height:${size}px` });
  const ctx = cv.getContext('2d');
  const k = Math.cos((phase / 8) * Math.PI * 2);
  const c = (N - 1) / 2, r = N / 2 - (smooth ? 2 : 1);
  for (let y = 0; y < N; y++) for (let x = 0; x < N; x++) {
    const px = (x - c) / r, py = (y - c) / r;
    if (px * px + py * py > 1) continue;
    const edge = Math.sqrt(Math.max(0, 1 - py * py));
    const lit = phase <= 4 ? px >= k * edge : px <= -k * edge;
    ctx.fillStyle = smooth ? (lit ? '#f2e8cc' : '#141826') : lit ? ((x + y) % 5 === 0 ? '#dcd8c0' : '#f4f1dc') : '#26304e';
    ctx.fillRect(x, y, 1, 1);
  }
  return cv;
};

// A small spiral shell icon in the shell's glass colour (for lists and finds).
export const shellIcon = (color, size = 36, rarity = 0, id = null) => {
  if (document.body.classList.contains('look-kagee')) return puppetShellIcon(color, size, rarity, id);
  const cv = h('canvas', { width: 24, height: 24, class: 'shell-icon', style: `width:${size}px;height:${size}px` });
  const ctx = cv.getContext('2d');
  ctx.fillStyle = color;
  ctx.beginPath(); ctx.ellipse(12, 13, 9, 7, -0.4, 0, Math.PI * 2); ctx.fill();
  ctx.strokeStyle = '#0b0910'; ctx.lineWidth = 1.5;
  ctx.beginPath();
  for (let a = 0; a < Math.PI * 5; a += 0.2) { const r = 1 + a * 0.55; const x = 12 + Math.cos(a) * r * 0.9, y = 12.5 + Math.sin(a) * r * 0.7; a ? ctx.lineTo(x, y) : ctx.moveTo(x, y); }
  ctx.stroke();
  ctx.beginPath(); ctx.ellipse(12, 13, 9, 7, -0.4, 0, Math.PI * 2); ctx.stroke();
  if (rarity >= 2) { ctx.fillStyle = '#fff6d0'; ctx.fillRect(18, 4, 2, 2); ctx.fillRect(19, 3, 1, 4); ctx.fillRect(17, 5, 4, 1); }
  return cv;
};

// 影絵: the shell as a black paper puppet, lit only through its own holes
// (the same motif the 3D puppet wears). Unknown shells show the bare silhouette.
function puppetShellIcon(color, size, rarity, id) {
  const N = 96;
  const cv = h('canvas', { width: N, height: N, class: 'shell-icon', style: `width:${size}px;height:${size}px` });
  const ctx = cv.getContext('2d');
  const kind = id ? SHELL_HOLES[id] ?? 0 : -1;
  const body = () => { ctx.beginPath(); ctx.ellipse(48, 52, 38, 29, -0.4, 0, Math.PI * 2); };
  ctx.save(); body(); ctx.fillStyle = '#05040a'; ctx.fill(); ctx.clip();
  ctx.fillStyle = color; ctx.strokeStyle = color; ctx.lineCap = 'round';
  const rnd = k => { const s = Math.sin(k * 127.1) * 43758.5453; return s - Math.floor(s); };
  if (kind === 0) for (let y = 30; y < 80; y += 13) for (let x = 18; x < 84; x += 13) { ctx.beginPath(); ctx.arc(x + (y % 26 ? 6 : 0), y, 3.6, 0, Math.PI * 2); ctx.fill(); }
  if (kind === 1) { ctx.lineWidth = 4.5; for (let k = -4; k <= 4; k++) { ctx.beginPath(); ctx.moveTo(10, 52 + k * 11 - 14); ctx.lineTo(86, 52 + k * 11 + 14); ctx.stroke(); } }
  if (kind === 2) for (let k = 0; k < 16; k++) { ctx.beginPath(); ctx.arc(18 + rnd(k) * 62, 28 + rnd(k + 40) * 48, 2.5 + rnd(k + 80) * 3.5, 0, Math.PI * 2); ctx.fill(); }
  if (kind === 3) { ctx.lineWidth = 4; ctx.beginPath(); for (let a = 0; a < Math.PI * 6; a += 0.1) { const r = 3 + a * 2.1; const x = 48 + Math.cos(a) * r, y = 52 + Math.sin(a) * r * 0.78; a ? ctx.lineTo(x, y) : ctx.moveTo(x, y); } ctx.stroke(); }
  if (kind === 4) { ctx.lineWidth = 4; for (let k = 0; k < 12; k++) { const a = -Math.PI * 0.95 + k * 0.17; ctx.beginPath(); ctx.moveTo(48 + Math.cos(a) * 10, 70 + Math.sin(a) * 10); ctx.lineTo(48 + Math.cos(a) * 60, 70 + Math.sin(a) * 60); ctx.stroke(); } }
  ctx.restore();
  if (rarity >= 2) { ctx.fillStyle = '#f6dc9a'; ctx.beginPath(); for (let k = 0; k < 8; k++) { const a = k * Math.PI / 4, r = k % 2 ? 3 : 10; ctx.lineTo(80 + Math.cos(a) * r, 16 + Math.sin(a) * r); } ctx.fill(); }
  return cv;
}

export const driftIcon = (id, size = 36) => {
  const cv = h('canvas', { width: 24, height: 24, class: 'shell-icon', style: `width:${size}px;height:${size}px` });
  const ctx = cv.getContext('2d');
  if (id === 'float') { ctx.fillStyle = document.body.classList.contains('look-kagee') ? '#e09a3c' : '#5fe0c8'; ctx.beginPath(); ctx.arc(12, 12, 8, 0, Math.PI * 2); ctx.fill(); ctx.strokeStyle = '#0b0910'; ctx.lineWidth = 1; for (let k = -8; k <= 8; k += 4) { ctx.beginPath(); ctx.moveTo(4, 12 + k); ctx.lineTo(20, 12 + k); ctx.stroke(); } }
  if (id === 'wood') { ctx.fillStyle = '#8a6a4a'; ctx.save(); ctx.translate(12, 12); ctx.rotate(-0.5); ctx.fillRect(-10, -3, 20, 6); ctx.fillStyle = '#5a3f2a'; ctx.fillRect(-10, -1, 20, 1); ctx.restore(); }
  if (id === 'net') { ctx.strokeStyle = '#c9b48a'; ctx.lineWidth = 1; for (let k = 3; k < 22; k += 4) { ctx.beginPath(); ctx.moveTo(k, 3); ctx.lineTo(k, 21); ctx.moveTo(3, k); ctx.lineTo(21, k); ctx.stroke(); } }
  if (id === 'seaglass') { const cs = ['#7fd0b0', '#7fa8e0', '#e0c070']; cs.forEach((c, i) => { ctx.fillStyle = c; ctx.beginPath(); ctx.ellipse(7 + i * 5, 12 + (i % 2) * 3, 4, 3, i, 0, Math.PI * 2); ctx.fill(); }); }
  if (id === 'pearl') { ctx.fillStyle = '#f4f0e4'; ctx.beginPath(); ctx.arc(12, 12, 6, 0, Math.PI * 2); ctx.fill(); ctx.fillStyle = '#fff'; ctx.fillRect(9, 9, 2, 2); }
  return cv;
};
